/**
 * Textes de l'habillage de l'interface.
 *
 * Règle non négociable n° 2 : aucun texte de jeu en dur dans un composant. Les
 * textes *du jeu* (situations, consignes, problèmes, feedbacks) viennent tous de
 * `@neopolis/content`. Restent les libellés de l'habillage — « Reprendre »,
 * « Texte agrandi » — que le contenu ne prévoit pas aujourd'hui.
 *
 * Ils sont rassemblés ici, et **nulle part ailleurs** : un test vérifie qu'aucun
 * composant ne contient de texte affichable. Le jour où le contenu accueillera
 * une section « ui », ce fichier n'aura qu'à être vidé dedans — c'est une
 * évolution du schéma, donc une décision de l'enseignant (règle n° 3).
 */
export const T = {
  accueil: {
    sousTitre: 'Le service technique de la ville, c’est vous.',
    labelCode: 'Votre code d’îlot',
    aideCode: '6 caractères, comme sur l’étiquette du maire.',
    codeInvalide: 'Ce code ne ressemble pas à un code d’îlot. Vérifiez l’étiquette.',
    codeInconnu:
      'Ce code n’est pas reconnu. Vérifiez chaque caractère sur l’étiquette, puis réessayez.',
    commencer: 'Commencer la partie',
    reprendre: 'Reprendre la partie',
    reprendreAide: 'Une partie est enregistrée sur cette tablette.',
    erreurReprise:
      'La partie enregistrée n’a pas pu être relue. Il faut en commencer une nouvelle.',
  },
  jauges: {
    titre: 'État de la ville',
    enHausse: 'en hausse',
    enBaisse: 'en baisse',
    stable: 'stable',
    variation: 'variation',
  },
  carte: {
    titre: 'Carte de Néopolis',
    legende: 'Touchez un quartier pour l’explorer.',
    quartierAmeliore: 'quartier équipé',
    quartierAExplorer: 'quartier à explorer',
  },
  maire: {
    rôle: 'Message du maire',
    fermer: 'Fermer le message',
    continuer: 'Continuer',
  },
  etapes: {
    titre: 'Étapes du chapitre',
    verrouillee: 'étape verrouillée',
    terminee: 'étape terminée',
    enCours: 'étape en cours',
    aller: 'Aller à l’étape',
  },
  explorer: {
    compteur: 'Problèmes repérés',
    minimum: 'Il en faut au moins',
    pourAvancer: 'pour passer à la suite.',
    quartierVide: 'Rien à signaler dans ce quartier.',
    repere: 'repéré',
    reperer: 'Noter ce problème',
    temoignage: 'Témoignage',
    donnee: 'Donnée chiffrée',
    fermerQuartier: 'Refermer le quartier',
  },
  classer: {
    consigneSelection: 'Choisissez un problème, puis sa catégorie.',
    aRanger: 'À ranger',
    selectionne: 'sélectionné',
    placerDans: 'Ranger dans',
    retirer: 'Retirer de sa catégorie',
    valider: 'Valider le classement',
    juste: 'bien rangé',
    faux: 'mal rangé',
    bonneReponse: 'La bonne catégorie était',
    secondeChance: 'Vous pouvez corriger celui-ci une fois.',
    plusDeChance: 'Plus de correction possible pour celui-ci.',
    resultat: 'Bien rangés',
  },
  analyser: {
    question: 'Un objet connecté peut-il aider ?',
    oui: 'Oui',
    enPartie: 'En partie',
    non: 'Non',
    explication: 'Ce qu’en pense le service technique',
    momentCle: 'Un objet connecté ne résout pas tout',
    votreReponse: 'Votre réponse',
  },
  decider: {
    consigne: 'Placez vos trois priorités sur le podium.',
    podium: 'Podium des priorités',
    place: 'Place',
    vide: 'Place libre',
    retirer: 'Retirer du podium',
    ajouter: 'Mettre sur le podium',
    justification: 'Pourquoi ces priorités ?',
    justificationAide: 'Expliquez votre choix au maire, en quelques phrases.',
  },
  rapport: {
    titre: 'Rapport de diagnostic',
    voir: 'Voir notre rapport',
    imprimer: 'Imprimer le rapport',
    fermer: 'Fermer le rapport',
    colonneProbleme: 'Problème',
    colonneQuartier: 'Quartier',
    colonneCategorie: 'Catégorie',
    colonneConnecte: 'Objet connecté utile ?',
    aRetenir: 'À retenir',
    score: 'Score du chapitre',
    points: 'points',
    ilot: 'Îlot',
    chapitreSuivant: 'Passer au chapitre suivant',
    chapitreVerrouille: 'Votre enseignant n’a pas encore ouvert le chapitre suivant.',
  },
  comprendre: {
    titreAnimation: 'Le lampadaire d’aujourd’hui, sur 24 heures',
    legendeAnimation: 'La lampe reste allumée toute la nuit, que la rue soit pleine ou déserte.',
    heure: 'Heure',
    rueVide: 'Rue vide',
    passants: 'passants',
    lampeAllumee: 'Lampe allumée',
    lampeEteinte: 'Lampe éteinte',
    rejouer: 'Revoir l’animation',
    questions: 'Ce que vous avez observé',
    bonneReponse: 'Bonne réponse',
    mauvaiseReponse: 'Ce n’est pas ça',
  },
  concevoir: {
    chaine: 'Chaîne d’information',
    bibliotheque: 'Bibliothèque d’équipements',
    filtreTous: 'Tous',
    caseVide: 'Poser un équipement ici',
    retirer: 'Retirer de la chaîne',
    ajouterDans: 'Poser dans',
    fiche: 'Voir la fiche',
    fermerFiche: 'Fermer la fiche',
    mesure: 'Ce qu’il mesure',
    role: 'Rôle dans la chaîne',
    cout: 'Coût',
    impactFabrication: 'Impact de fabrication',
    effetPermanent: 'Effet permanent',
    usages: 'Utile pour',
    coutTotal: 'Coût de notre chaîne',
    budget: 'Budget de la ville',
    tropCher: 'Le coût dépasse le budget.',
    obligatoire: 'obligatoire',
  },
  tester: {
    simuler: 'Simuler une nuit',
    rejouer: 'Rejouer la nuit',
    enCours: 'Simulation en cours',
    consommation: 'Consommation de la nuit',
    avant: 'Éclairage actuel',
    apres: 'Notre solution',
    unite: 'heures de pleine lumière',
    deployer: 'Installer dans la ville',
    deploye: 'Solution installée',
    etoiles: 'Qualité de la solution',
    variation: 'Effet sur la ville',
  },
  ameliorer: {
    evenement: 'Il se passe quelque chose',
    debat: 'À discuter avec la classe',
    choisir: 'Choisir',
    revenirConcevoir: 'Revoir notre chaîne',
    revenirAide: 'Vous pouvez revenir une seule fois sur votre conception.',
    plusDeRetour: 'Vous avez déjà utilisé votre retour à la conception.',
    termine: 'Tous les événements sont passés.',
  },
  fiche: {
    voir: 'Voir notre fiche technique',
    schema: 'Notre chaîne d’information',
    avantApres: 'La ville avant et après',
    colonneJauge: 'Indicateur',
    colonneAvant: 'Avant',
    colonneApres: 'Après',
    colonneEcart: 'Écart',
    solutionRetenue: 'Solution retenue',
    coutPaye: 'Coût payé',
  },
  sauvegarde: {
    synchronise: 'Partie enregistrée',
    enAttente: 'Enregistrement…',
    horsLigne: 'Hors ligne, la partie est gardée sur la tablette',
    conflit: 'Cette partie a été modifiée ailleurs. Rechargez la page avant de continuer.',
  },
  reparer: {
    titre: 'Le programme de la ville',
    aide: 'Lisez-le d’abord. Que déclenche-t-il, et qu’est-ce qu’il ne regarde jamais ?',
    chaqueJour: 'CHAQUE JOUR',
    uniteHeure: 'h',
    uniteMinutes: 'min',
    a: 'à',
    arroserPendant: 'arroser pendant',
    si: 'SI',
    alors: 'ALORS',
    sansCondition: 'Ce programme ne lit aucun capteur.',
    ajouterCondition: 'Ajouter une condition',
    retirerCondition: 'Retirer la condition',
    choisirHeure: 'Heure de déclenchement',
    choisirDuree: 'Durée de l’arrosage',
    choisirGrandeur: 'Grandeur mesurée',
    choisirComparateur: 'Sens de la comparaison',
    choisirSeuil: 'Seuil de déclenchement',
    mesureePar: 'mesurée par',
    inchange: 'Vous n’avez encore rien changé au programme de la ville.',
    modifie: 'Programme modifié. Passez à l’étape suivante pour l’éprouver sur une semaine.',
  },

  semaine: {
    titre: 'Une semaine avec votre programme',
    lancer: 'Lancer la semaine',
    relancer: 'Relancer la semaine',
    enCours: 'Semaine en cours…',
    colonneJour: 'Jour',
    colonneMeteo: 'Météo',
    colonneSol: 'Terre le matin',
    colonneArrosage: 'Arrosage',
    colonneSoir: 'Terre le soir',
    arrose: 'oui',
    pasArrose: 'non',
    litres: 'litres',
    perdus: 'perdus par évaporation',
    pluie: 'mm de pluie',
    bilanEau: 'Eau consommée sur la semaine',
    bilanPerdue: 'Eau perdue avant d’atteindre les racines',
    bilanSouffrance: 'Jours où les plantes ont manqué d’eau',
    plantesOk: 'Les plantes ont tenu toute la semaine.',
    etoiles: 'Qualité du programme',
    aucunProgramme: 'Réparez d’abord le programme, à l’étape précédente.',
  },

  fiche3: {
    programme: 'Le programme que nous avons écrit',
    bilan: 'Ce que notre programme a changé sur une semaine',
    eauAvant: 'Programme de la ville',
    eauApres: 'Notre programme',
    arrosages: 'jours d’arrosage sur 7',
    economie: 'Eau économisée',
  },

  tournee: {
    titre: 'Organiser la tournée',
    aide: 'Les capteurs vous disent où en est chaque conteneur. Cochez ceux que le camion videra ce jour-là.',
    jour: 'Sortie du',
    remplissage: 'Remplissage',
    collecter: 'Vider ce conteneur',
    arretsChoisis: 'arrêts choisis',
    camionReste: 'Le camion ne sort pas ce jour-là.',
    coutDuJour: 'Cette sortie',
    valider: 'Valider cette sortie',
    jourSuivant: 'Jour suivant',
    semaineOrganisee: 'Les trois sorties sont organisées. Passez à l’étape suivante pour le bilan.',
    pleinBientot: 'ce conteneur approche de la saturation',
    deborde: 'ce conteneur déborde',
  },

  bilanCollecte: {
    titre: 'Le bilan de la semaine',
    colonneJour: 'Sortie',
    colonneArrets: 'Arrêts',
    colonneKm: 'Distance',
    colonneCarburant: 'Carburant',
    colonneCo2: 'CO₂',
    km: 'km',
    litres: 'L',
    kgCo2: 'kg',
    totalVotre: 'Votre semaine',
    totalActuelle: 'La tournée fixe d’aujourd’hui',
    economie: 'Économie',
    debordements: 'Conteneurs qui ont débordé',
    aucunDebordement: 'Aucun conteneur n’a débordé.',
    aucuneTournee: 'Organisez d’abord la semaine, à l’étape précédente.',
  },

  fiche4: {
    tournees: 'Nos trois sorties',
    bilan: 'Ce que notre organisation a changé',
    avant: 'Tournée fixe',
    apres: 'Nos tournées',
  },

  prof: {
    titre: 'Espace enseignant',
    retourJeu: 'Retour au jeu',

    creerClasse: 'Créer une classe',
    nomClasse: 'Nom de la classe',
    nomClasseAide: 'Par exemple : 4B.',
    pin: 'Code PIN',
    pinAide: '4 à 6 chiffres. Il vous sera demandé pour suivre la classe.',
    nombreIlots: 'Nombre d’îlots',
    nombreIlotsAide: 'Un code par groupe de 3 ou 4 élèves.',
    creer: 'Créer la classe et ses codes',
    creationImpossible: 'La classe n’a pas pu être créée. Vérifiez votre saisie.',

    ouvrirClasse: 'Ouvrir une classe existante',
    codeClasse: 'Code de la classe',
    ouvrir: 'Ouvrir le suivi',
    ouvertureImpossible: 'Code de classe ou PIN incorrect.',

    planche: 'Codes à découper',
    plancheAide: 'Imprimez cette planche et donnez un code à chaque îlot.',
    imprimerPlanche: 'Imprimer les codes',
    allerAuSuivi: 'Aller au suivi de la classe',
    conserverCode: 'Notez le code de classe et le PIN : ils ne sont plus affichés ensuite.',

    suivi: 'Suivi de la classe',
    colonneIlot: 'Îlot',
    colonneChapitre: 'Chapitre',
    colonneEtape: 'Étape',
    colonneJauges: 'Indicateurs',
    colonneSolution: 'Solution installée',
    colonneTemps: 'Temps passé',
    colonneActions: 'Actions',
    pasCommence: 'Pas commencé',

    reinitialiserIlot: 'Remettre à zéro',
    reinitialiserIlotDe: 'Remettre à zéro l’îlot',
    reinitialiserConfirmation:
      'Effacer la partie de cet îlot ? Son code reste le même : le groupe repart du début avec la même étiquette.',
    reinitialiserEchec: 'La remise à zéro n’a pas abouti. Réessayez dans un instant.',

    supprimerClasse: 'Supprimer la classe',
    supprimerClasseConfirmation:
      'Effacer cette classe, ses îlots et toutes leurs parties ? Cette action est définitive.',
    supprimerClasseEchec: 'La suppression n’a pas abouti. Réessayez dans un instant.',
    classeSupprimee: 'Classe supprimée.',
    aucuneSolution: '—',
    rafraichi: 'Actualisé',
    rafraichirMaintenant: 'Actualiser',

    chapitres: 'Chapitres ouverts aux élèves',
    ouvrirChapitre: 'Ouvrir',
    fermerChapitre: 'Fermer',

    projection: 'Mode projection',
    quitterProjection: 'Quitter la projection',
    solutionsChoisies: 'Les solutions choisies par la classe',
    aucuneSolutionEncore: 'Aucun îlot n’a encore installé sa solution.',

    exporterCsv: 'Exporter en CSV',

    analyse: 'Analyse de séance',
    quitterAnalyse: 'Retour au suivi',
    analyseIlots: 'Îlots ayant joué',
    tempsParEtape: 'Temps médian par étape',
    colonneTempsMedian: 'Temps médian',
    colonneArrives: 'Arrivés',
    colonneTermines: 'Terminés',
    blocages: 'Où les îlots bloquent',
    aucunBlocage: 'Aucun îlot n’est resté bloqué sur une étape.',
    ilotsBloques: 'îlots bloqués',
    erreursFrequentes: 'Erreurs les plus fréquentes',
    aucuneErreur: 'Aucune erreur enregistrée.',
    occurrences: 'fois',
    surIlots: 'îlots',
    erreurClassement: 'Classement',
    erreurChaine: 'Chaîne d’information',
    retoursQuestionnaire: 'Ce qu’en disent les îlots',
    aucuneReponse: 'Aucun questionnaire rempli.',
    difficultes: 'Ce qui était difficile',
  },

  questionnaire: {
    titre: 'Avant de partir, trois questions',
    compris: 'On a compris ce chapitre',
    aime: 'On a aimé ce chapitre',
    difficile: 'Ce qui était difficile',
    difficileAide: 'En une phrase. N’écrivez pas votre prénom.',
    peu: 'Pas trop',
    moyen: 'Moyen',
    bien: 'Bien',
    envoyer: 'Envoyer nos réponses',
    merci: 'Merci, vos réponses sont enregistrées.',
  },
  mentions: {
    titre: 'Mentions et données',
    lien: 'Mentions et données',
    retour: 'Retour',

    donneesTitre: 'Quelles données sont enregistrées ?',
    donneesTexte:
      'Néopolis n’enregistre aucune donnée personnelle. Un groupe d’élèves est identifié par un code d’îlot de six caractères, distribué en classe sur papier. Aucun nom, aucun prénom, aucune adresse électronique, aucune date de naissance n’est demandé ni conservé.',

    classeTitre: 'Et pour l’enseignant ?',
    classeTexte:
      'Une classe est enregistrée sous le nom libre que l’enseignant lui donne — par exemple « 4B » — avec un code PIN, stocké sous forme chiffrée et jamais lisible. Rien n’oblige à y mettre un nom de personne.',

    cookiesTitre: 'Cookies et traceurs',
    cookiesTexte:
      'Aucun cookie de mesure d’audience, aucun traceur publicitaire, aucun service tiers. La partie en cours est conservée dans la mémoire du navigateur de la tablette, pour que le jeu survive à une coupure de réseau.',

    hebergementTitre: 'Hébergement',
    hebergementTexte:
      'Les données sont hébergées en France, sur un serveur dédié au projet. Aucune ressource n’est chargée depuis un service extérieur : polices, icônes et images font partie de l’application.',

    conservationTitre: 'Durée de conservation',
    conservationTexte:
      'Les parties d’une classe sont effacées automatiquement après douze mois sans activité. Un enseignant peut aussi demander leur suppression immédiate.',

    exportTitre: 'Export des résultats',
    exportTexte:
      'L’export en CSV proposé à l’enseignant ne contient que des codes d’îlot, des chapitres et des chiffres de jeu.',
  },
  accessibilite: {
    texteAgrandi: 'Texte agrandi',
    texteNormal: 'Texte normal',
    contenuPrincipal: 'Contenu principal',
    quitter: 'Quitter la partie',
  },
} as const;
